/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

// Code generated by the Google Gen AI SDK generator DO NOT EDIT.

import {ApiClient} from './_api_client.js';
import * as common from './_common.js';
import {BaseModule} from './_common.js';
import * as converters from './converters/_documents_converters.js';
import {PagedItem, PagedItemConfig, Pager} from './pagers.js';
import * as types from './types.js';

export class Documents extends BaseModule {
  constructor(private readonly apiClient: ApiClient) {
    super();
  }

  /**
   * Lists documents.
   *
   * @param params - The parameters for the list request.
   * @return - A pager of documents.
   *
   * @example
   * ```ts
   * const documents = await ai.documents.list({parent:'rag_store_name', config: {'pageSize': 2}});
   * for await (const document of documents) {
   *   console.log(document);
   * }
   * ```
   */

  list = async (
    params: types.ListDocumentsParameters,
  ): Promise<Pager<types.Document>> => {
    return new Pager<types.Document>(
      PagedItem.PAGED_ITEM_DOCUMENTS,
      (x: PagedItemConfig) =>
        this.listInternal({parent: params.parent, config: x.config}),
      await this.listInternal(params),
      params,
    );
  };

  /**
   * Gets a Document.
   *
   * @param params - The parameters for getting a document.
   * @return Document.
   */

  async get(params: types.GetDocumentParameters): Promise<types.Document> {
    let response: Promise<types.Document>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.getDocumentParametersToMldev(params);
      path = common.formatMap(
        '{name}',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'GET',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.Document>;

      return response.then((resp) => {
        return resp as types.Document;
      });
    }
  }

  /**
   * Deletes a Document.
   *
   * @param params - The parameters for deleting a document.
   */

  async delete(params: types.DeleteDocumentParameters): Promise<void> {
    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.deleteDocumentParametersToMldev(params);
      path = common.formatMap(
        '{name}',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      await this.apiClient.request({
        path: path,
        queryParams: queryParams,
        body: JSON.stringify(body),
        httpMethod: 'DELETE',
        httpOptions: params.config?.httpOptions,
        abortSignal: params.config?.abortSignal,
      });
    }
  }

  private async listInternal(
    params: types.ListDocumentsParameters,
  ): Promise<types.ListDocumentsResponse> {
    let response: Promise<types.ListDocumentsResponse>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.listDocumentsParametersToMldev(params);
      path = common.formatMap(
        '{parent}/documents',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'GET',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.ListDocumentsResponse>;

      return response.then((apiResponse) => {
        const resp = converters.listDocumentsResponseFromMldev(apiResponse);
        const typedResp = new types.ListDocumentsResponse();
        Object.assign(typedResp, resp);
        return typedResp;
      });
    }
  }
}
